import { Button, Divider, Flex, Group, Modal, Select } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { z } from 'zod';
import { GenericErrorModal } from '../error/GenericErrorModal';
import PageLoader from '../UI/loader/PageLoader';
import { useClientsContext } from './context/ClientsContext';
import { useAdvisors } from './hooks/useAdvisors';
import { useEffect } from 'react';
import CreateClientForm from 'src/clients/CreateClientForm';
import ModalBackButton from 'src/UI/common/ModalBackButton';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { formatName } from 'src/utils/nameFormatting';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useClients } from './hooks/useClients';

type Props = {
  opened: boolean;
  close: () => void;
  canGoBack?: boolean;
  previousStep?: () => void;
  emailAddress?: string;
  prefill?: { firstName?: string; lastName?: string; emailAddress?: string };
  navigateAfterCreation?: boolean;
  onClientCreated?: () => void;
};

const formSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  emailAddress: z.string().trim().email('Please enter a valid email address').nullable().optional(),
  advisorUserId: z.string().min(1, 'Please select an adviser'),
});

export default function CreateClientModal({
  opened,
  close,
  canGoBack,
  previousStep,
  emailAddress,
  prefill,
  navigateAfterCreation = true,
  onClientCreated = () => {},
}: Props) {
  const { currentUser, isSystemAdmin, isServiceAdmin } = useCurrentUser();
  const { advisors, isLoading: isAdvisorsLoading } = useAdvisors(opened);
  const { createClient, error, isLoading } = useClientsContext();
  const { flagEnabledPaginatedClients } = useFlags();
  const { clients, isLoading: isClientsLoading } = useClients(
    {},
    { enabled: opened && !flagEnabledPaginatedClients },
  );

  const isDuplicateEmail = (email: string) =>
    clients.some(
      client => client.emailAddress?.trim().toLowerCase() === email.trim().toLowerCase(),
    );

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      firstName: '',
      lastName: '',
      emailAddress: emailAddress || '',
      advised: true,
      advisorUserId: '',
    },
    validate: values => {
      const zodErrors = schemaResolver(formSchema, { sync: true })(values);
      if (!flagEnabledPaginatedClients) {
        if (
          !zodErrors.emailAddress &&
          values.emailAddress &&
          isDuplicateEmail(values.emailAddress)
        ) {
          zodErrors.emailAddress = 'This email address is already in use.';
        }
      }
      return zodErrors;
    },
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (opened && prefill) {
      if (prefill.firstName) form.setFieldValue('firstName', prefill.firstName);
      if (prefill.lastName) form.setFieldValue('lastName', prefill.lastName);
      if (prefill.emailAddress) form.setFieldValue('emailAddress', prefill.emailAddress);
    }
  }, [opened, prefill]);

  useEffect(() => {
    if (opened && currentUser?.id && !form.values.advisorUserId) {
      form.setFieldValue('advisorUserId', currentUser.id);
    }
  }, [opened, currentUser?.id]);

  const onCreateClient = async (values: {
    emailAddress: string | null | undefined;
    firstName: string;
    lastName: string;
    advisorUserId: string;
    advised: boolean;
  }) => {
    try {
      await createClient(values, navigateAfterCreation);
      dispatchAppEvent('CLIENT_CREATED');
      close();
      form.reset();
      onClientCreated();
    } catch (e) {
      if (ApiError.isApiError(ApiErrorCode.DUPLICATE_EMAIL, e)) {
        form.setFieldError('emailAddress', e.message);
        return;
      }
      notifyAndLogError(
        'Failed to create client',
        'An error occurred while creating the client. Please try again.',
        e,
        { clientData: values },
      );
    }
  };

  if (error instanceof ApiError && !ApiError.isApiError(ApiErrorCode.DUPLICATE_EMAIL, error)) {
    return <GenericErrorModal opened={true} onClose={close} />;
  }

  return (
    <Modal opened={opened} onClose={close} title="Client details" centered>
      {previousStep && (
        <ModalBackButton previousStep={previousStep} canGoBack={Boolean(canGoBack)} />
      )}
      {isLoading || isAdvisorsLoading || isClientsLoading ? (
        <PageLoader absolute={false} />
      ) : (
        <form onSubmit={form.onSubmit(values => onCreateClient(values))}>
          <Flex direction="column" gap="md">
            <CreateClientForm form={form} />
            {/*Having typing issues with this select in the CreateClientForm so I extracted it and duplicated it*/}
            <Select
              label="Is this person a client?"
              placeholder="Please select"
              data={[
                { value: 'true', label: 'Yes, they are an advised client' },
                { value: 'false', label: 'No, they are a contact' },
              ]}
              onChange={value => form.setFieldValue('advised', value === 'true')}
              value={form.values.advised?.toString()}
            />

            {(isSystemAdmin || isServiceAdmin) && (
              <>
                <Divider my="md" color="black" />
                <Select
                  label="Adviser"
                  placeholder="Select adviser"
                  data={advisors.map(advisor => ({
                    value: advisor.id,
                    label: formatName(advisor.firstName, advisor.lastName),
                  }))}
                  {...form.getInputProps('advisorUserId')}
                />
              </>
            )}

            <Group justify="flex-end" mt="md">
              <Button type="submit" variant="filled-pill-black" disabled={!form.isValid()}>
                Add
              </Button>
            </Group>
          </Flex>
        </form>
      )}
    </Modal>
  );
}
